import Button from '@mui/material/Button';
import { FC, Fragment } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DecisionStrategy,
  IPolicyRepresentation,
  Logic,
} from '@bom-nextgen-keycloak/models';
import {
  selectPermissionPolicyDetail,
  useResource,
} from '@bom-nextgen-keycloak/web/core';
import {
  PolicyDeleteDialog as DeleteDialog,
  usePolicy2,
} from '@bom-nextgen-keycloak/web/pages/feature-policy';
import { FeaturePage } from '@bom-nextgen-keycloak/web/shared/data-access';
import {
  ProtectedRouter,
  TabbedContent,
  TabList,
} from '@bom-nextgen-keycloak/web/shared/ui';
import { usePolicyDetail } from './hooks';
import { PARENT_PATH } from './shared';

const PolicyDetail3: FC = () => {
  const { id = '', type = '' } = useParams();
  const navigate = useNavigate();
  const { clientId } = useResource();
  const {
    canDelete,
    isDeleteDialogOpen,
    isDeleting,
    handleDeleteAction,
    handleDeleteCancel,
    handleDeleteRequest,
  } = usePolicy2({
    callback: () => navigate(PARENT_PATH),
    disableQuery: true,
  });
  const { data, isLoading } = usePolicyDetail(clientId, id, type);
  const { canViewGeneralTab } = useSelector(selectPermissionPolicyDetail);
  const parentPath = PARENT_PATH;
  const rootPath = `${parentPath}/${id}/${type}`;
  const showDeleteButton = canDelete;
  const disableDeleteButton = isLoading;

  const tabList: TabList = [
    {
      enabled: canViewGeneralTab,
      label: 'General',
      value: '',
    },
  ];

  const itemRepresentation: IPolicyRepresentation = {
    config: data?.config,
    decisionStrategy: data?.decisionStrategy as DecisionStrategy,
    description: data?.description ?? '',
    id: data?.id ?? '',
    logic: data?.logic as Logic,
    name: data?.name ?? '',
    owner: data?.owner,
    policies: data?.policies,
    resources: data?.resources,
    roles: data?.roles ?? [],
    type: data?.type ?? '',
    users: data?.users,
  };

  const handleDelete = () => {
    handleDeleteRequest(itemRepresentation);
  };

  const renderActions = () => {
    return showDeleteButton ? (
      <Button
        color="error"
        disabled={disableDeleteButton}
        onClick={handleDelete}
        variant="text"
      >
        Delete
      </Button>
    ) : null;
  };

  return (
    <Fragment>
      <TabbedContent
        actions={renderActions()}
        breadcrumb={data?.name}
        feature="Policies"
        parentPath={parentPath}
        rootPath={rootPath}
        tabList={tabList}
      />
      <DeleteDialog
        loading={isDeleting}
        name={itemRepresentation?.name}
        onClose={handleDeleteCancel}
        onSubmit={handleDeleteAction}
        open={isDeleteDialogOpen}
      />
    </Fragment>
  );
};

const WrappedPolicyDetailPage: FC = () => {
  const { canViewPage } = useSelector(selectPermissionPolicyDetail);

  return (
    <ProtectedRouter
      canView={canViewPage}
      feature={FeaturePage.POLICY}
      isEditPage
      navigatePath={PARENT_PATH}
    >
      <PolicyDetail3 />
    </ProtectedRouter>
  );
};

export { WrappedPolicyDetailPage };
